import {AstView, EnrichedAstNode, SpacingStrategy, Direction, DistanceProp, CSSProperty} from '../types';
import {visitor, roundNumber} from '../helpers';
import {SizeUnit} from '../UnitConversion';
import {convertCssUnit, absolutePositionSingleChildren} from '../';

type ParentDisplay = 'Block' | 'Flex';

export const getBoxSizingAwareWidthAndHeight = (node: EnrichedAstNode) => {
    if (node.calculatedLayoutAbstraction?.borderBox === 'border-box') {
        return {
            width: node.position.width,
            height: node.position.height,
        };
    }
    return {
        width: node.innerPosition.width,
        height: node.innerPosition.height,
    };
};

export function applySizingFromSettings(tree: EnrichedAstNode, view: AstView) {
    visitor(tree, view.meta, (node, meta, parent) => {
        if (parent?.type === 'Text') {
            return;
        }

        const parentMode: ParentDisplay = parent ? 'Flex' : 'Block';

        const calculatedSettings = node.calculatedLayoutAbstraction;

        if (!calculatedSettings) {
            throw new Error('No settings');
        }

        if (node.type === 'Frame' && node.children.length > 0) {
            node.styles.flexDirection = node.calculatedLayoutAbstraction.flexDirection;
        } else {
            delete node.styles.flexDirection;
        }

        node.styles.boxSizing = calculatedSettings.borderBox;
        if (node.children.length > 0) {
            node.styles.justifyContent = calculatedSettings.justifyContent;
        }
        if (calculatedSettings.maxWidth) {
            const {width} = getBoxSizingAwareWidthAndHeight(node);
            if (!node.styles.maxWidth) {
                node.styles.maxWidth = width;
            }
        } else {
            delete node.styles.maxWidth;
        }

        applyMargin(view, node);
        applyPadding(view, node);
        applyBorder(view, node);
        applyGap(view, node);

        if (calculatedSettings.absolutePosition) {
            applyAndConvertBoxSizingAwarePositionProperty(view, node, 'height');
            applyAndConvertBoxSizingAwarePositionProperty(view, node, 'width');
            if (parent) {
                absolutePositionSingleChildren(view, parent, node);
            }
            node.styles.position = 'absolute';
            return;
        } else {
            node.styles.position = 'relative';
            delete node.styles.top;
            delete node.styles.left;
            delete node.styles.right;
            delete node.styles.bottom;
        }

        if (parentMode === 'Block') {
            // width
            applyWidthBlockParent(view, node);
            // height
            applyHeightBlockParent(view, node);
        }
        if (parentMode === 'Flex' && parent) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const flexDirection = parent.calculatedLayoutAbstraction!.flexDirection;
            node.styles.alignSelf = calculatedSettings.alignSelf;
            applyAlignSelf(node, flexDirection);
            // width
            applyDistance(view, node, parent, 'width', flexDirection);
            // height
            applyDistance(view, node, parent, 'height', flexDirection);
        }

        if (
            node.type === 'Image' &&
            node.calculatedLayoutAbstraction?.widthSpacingStrategy === 'fill' &&
            node.calculatedLayoutAbstraction.preserveAspectRatio
        ) {
            node.styles.maxWidth = '100%';
            node.styles.height = 'auto';
        }
    });
}

const applyAlignSelf = (node: EnrichedAstNode, parentFlexDirection: Direction) => {
    const workingProperty = parentFlexDirection === 'row' ? 'heightSpacingStrategy' : 'widthSpacingStrategy';

    if (node.calculatedLayoutAbstraction[workingProperty] !== 'stretch') {
        node.styles.alignSelf = node.calculatedLayoutAbstraction.alignSelf;
    } else {
        node.styles.alignSelf = 'stretch';
    }
};

const applyDistance = (
    view: AstView,
    node: EnrichedAstNode,
    parent: EnrichedAstNode,
    property: DistanceProp,
    parentFlexDirection: Direction
) => {
    const isPrimaryAxisOfParent = getIsPrimaryAxis(parentFlexDirection, property);

    const spacingStrategy =
        property === 'width'
            ? node.calculatedLayoutAbstraction!.widthSpacingStrategy
            : node.calculatedLayoutAbstraction!.heightSpacingStrategy;

    if (isPrimaryAxisOfParent) {
        applyStylesFromAbstractionDistancePrefForPrimaryAxis(view, node, parent, spacingStrategy, property);
    } else {
        applyStylesFromAbstractionDistancePrefForSecondaryAxis(view, node, spacingStrategy, property);
    }
};

export const getIsPrimaryAxis = (direction: Direction, property: 'height' | 'width') => {
    if (direction === 'row') {
        return property === 'width';
    }
    return property === 'height';
};

const applyStylesFromAbstractionDistancePrefForPrimaryAxis = (
    view: AstView,
    node: EnrichedAstNode,
    parent: EnrichedAstNode,
    spacingStrategy: SpacingStrategy,
    distanceProp: DistanceProp
) => {
    delete node.styles[distanceProp];
    delete node.styles.flexShrink;

    if (spacingStrategy === 'fill' || spacingStrategy === 'stretch') {
        //calculateFillSize(node, parent, distanceProp);
        applyAndConvertBoxSizingAwarePositionProperty(view, node, distanceProp);
    } else if (spacingStrategy === 'hug') {
        // Do nothing
    } else if (spacingStrategy === 'fixed') {
        applyAndConvertBoxSizingAwarePositionProperty(view, node, distanceProp);
    }
};

const applyStylesFromAbstractionDistancePrefForSecondaryAxis = (
    view: AstView,
    node: EnrichedAstNode,
    spacingStrategy: SpacingStrategy,
    distanceProp: DistanceProp
) => {
    delete node.styles[distanceProp];

    if (spacingStrategy === 'fill') {
        // align stretch
        applyAndConvertBoxSizingAwarePositionProperty(view, node, distanceProp);
    } else if (spacingStrategy === 'hug') {
        // not align stretch
    } else if (spacingStrategy === 'fixed') {
        applyAndConvertBoxSizingAwarePositionProperty(view, node, distanceProp);
    } else if (spacingStrategy === 'stretch') {
        node.styles.alignSelf = 'stretch';
    }
};

export const applyWidthBlockParent = (view: AstView, node: EnrichedAstNode) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const abs = node.calculatedLayoutAbstraction!.widthSpacingStrategy;

    delete node.styles.width;

    if (abs === 'fill') {
        // Do nothing
        applyAndConvertBoxSizingAwarePositionProperty(view, node, 'width');
    } else if (abs === 'fixed') {
        applyAndConvertBoxSizingAwarePositionProperty(view, node, 'width');
    } else if (abs === 'hug') {
        node.styles.width = 'min-content';
    }
};

export const applyHeightBlockParent = (view: AstView, node: EnrichedAstNode) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const abs = node.calculatedLayoutAbstraction!.heightSpacingStrategy;

    delete node.styles.height;
    delete node.styles.minHeight;

    const {height} = getBoxSizingAwareWidthAndHeight(node);

    if (abs === 'fill') {
        node.styles.height = `100%`;
        node.styles.minHeight = `${roundNumber(height)}px`;
    } else if (abs === 'fixed') {
        applyAndConvertBoxSizingAwarePositionProperty(view, node, 'height');
    } else if (abs === 'hug') {
        // Do nothing
    }
};

export const applyAndConvertValue = (
    view: AstView,
    node: EnrichedAstNode,
    key: CSSProperty,
    valueInPX: number,
    removeIfZero?: boolean
) => {
    const isZero = valueInPX === 0;
    if (!isZero) {
        node.styles[key] = `${roundNumber(valueInPX)}px`;

        convertCssUnit({
            key: key,
            nodeId: node.internalId,
            view,
            toUnit: node.calculatedLayoutAbstraction!.units[key] || 'px',
            assignNewValueToNode: true,
        });
    } else if (removeIfZero && isZero) {
        delete node.styles[key];
    }
};

export const applyAndConvertBoxSizingAwarePositionProperty = (
    view: AstView,
    node: EnrichedAstNode,
    key: 'width' | 'height'
) => {
    if (node.calculatedLayoutAbstraction[key]) {
        node.styles[key] = node.calculatedLayoutAbstraction[key];
        return;
    }
    const property = getBoxSizingAwareWidthAndHeight(node)[key];

    node.styles[key] = `${roundNumber(property)}px`;
    convertCssUnit({
        key: key,
        nodeId: node.internalId,
        view,
        toUnit: node.calculatedLayoutAbstraction!.units[key] || 'px',
        assignNewValueToNode: true,
    });
};

export const applyFixedDimProperty = (view: AstView, node: EnrichedAstNode) => {
    applyAndConvertBoxSizingAwarePositionProperty(view, node, 'width');
    applyAndConvertBoxSizingAwarePositionProperty(view, node, 'height');
};

interface StyleApplicationIterator {
    value: number;
    unit: SizeUnit | undefined;
    key: CSSProperty;
}

const applyStylesFromAbstraction = (
    view: AstView,
    node: EnrichedAstNode,
    iterator: Array<StyleApplicationIterator>,
    removeIfZero = true
) => {
    iterator.forEach(({value, key}) => {
        applyAndConvertValue(view, node, key, value, removeIfZero);
    });
};

export const applyPadding = (view: AstView, node: EnrichedAstNode) => {
    const {paddingTop, paddingRight, paddingLeft, paddingBottom} = node.innerPosition;

    const iterator: Array<StyleApplicationIterator> = [
        {
            value: paddingTop,
            unit: node.calculatedLayoutAbstraction!.units.paddingTop || node.calculatedLayoutAbstraction!.units.padding,
            key: 'paddingTop',
        },
        {
            value: paddingBottom,
            unit:
                node.calculatedLayoutAbstraction!.units.paddingBottom ||
                node.calculatedLayoutAbstraction!.units.padding,
            key: 'paddingBottom',
        },
        {
            value: paddingLeft,
            unit:
                node.calculatedLayoutAbstraction!.units.paddingLeft || node.calculatedLayoutAbstraction!.units.padding,
            key: 'paddingLeft',
        },
        {
            value: paddingRight,
            unit:
                node.calculatedLayoutAbstraction!.units.paddingRight || node.calculatedLayoutAbstraction!.units.padding,
            key: 'paddingRight',
        },
    ];

    applyStylesFromAbstraction(view, node, iterator);
};

export const applyMargin = (view: AstView, node: EnrichedAstNode) => {
    const {marginTop, marginRight, marginLeft, marginBottom} = node.position;

    const iterator: Array<StyleApplicationIterator> = [
        {
            value: marginTop,
            unit: node.calculatedLayoutAbstraction!.units.marginTop || node.calculatedLayoutAbstraction!.units.margin,
            key: 'marginTop',
        },
        {
            value: marginBottom,
            unit:
                node.calculatedLayoutAbstraction!.units.marginBottom || node.calculatedLayoutAbstraction!.units.margin,
            key: 'marginBottom',
        },
        {
            value: marginLeft,
            unit: node.calculatedLayoutAbstraction!.units.marginLeft || node.calculatedLayoutAbstraction!.units.margin,
            key: 'marginLeft',
        },
        {
            value: marginRight,
            unit: node.calculatedLayoutAbstraction!.units.marginRight || node.calculatedLayoutAbstraction!.units.margin,
            key: 'marginRight',
        },
    ];

    applyStylesFromAbstraction(view, node, iterator);
};

export const applyBorder = (view: AstView, node: EnrichedAstNode) => {
    const {borderTopWidth, borderRightWidth, borderLeftWidth, borderBottomWidth} = node.position;

    const iterator: Array<StyleApplicationIterator> = [
        {
            value: borderTopWidth,
            unit: node.calculatedLayoutAbstraction!.units.borderTop || node.calculatedLayoutAbstraction!.units.border,
            key: 'borderTopWidth',
        },
        {
            value: borderBottomWidth,
            unit:
                node.calculatedLayoutAbstraction!.units.borderBottom || node.calculatedLayoutAbstraction!.units.border,
            key: 'borderBottomWidth',
        },
        {
            value: borderLeftWidth,
            unit: node.calculatedLayoutAbstraction!.units.borderLeft || node.calculatedLayoutAbstraction!.units.border,
            key: 'borderLeftWidth',
        },
        {
            value: borderRightWidth,
            unit: node.calculatedLayoutAbstraction!.units.borderRight || node.calculatedLayoutAbstraction!.units.border,
            key: 'borderRightWidth',
        },
    ];

    applyStylesFromAbstraction(view, node, iterator, false);
};

const applyGap = (view: AstView, node: EnrichedAstNode) => {
    applyStylesFromAbstraction(view, node, [
        {
            value: node.innerPosition.gap,
            unit: node.calculatedLayoutAbstraction!.units.gap,
            key: 'gap',
        },
    ]);
};
